import { Component, Host, Prop, h, Element, Watch, EventEmitter, Event, AttachInternals } from '@stencil/core';
import '@material/web/button/filled-button';
import '@material/web/button/outlined-button';
import '@material/web/button/text-button';
import '@material/web/icon/icon';
import '@material/web/ripple/ripple';

@Component({
  tag: 'omk-button',
  styleUrl: 'omk-button.scss',
  shadow: false,
  scoped: true,
  formAssociated: true,
})
export class OmkButton {
  @Prop() buttonType: string = '';
  @Prop({ reflect: true }) type: 'submit' | 'reset' | 'button' = 'submit';
  @Prop() buttonSize: 'large' | 'medium' | 'small' = 'large';
  @Prop({ reflect: true }) ghostMode: boolean = false;
  @Prop() disabled: boolean = false;
  @Prop() trailingIcon: boolean = false;
  @Prop() ariaLabel: string | null;
  @Prop() form: string = '';
  @Prop() name: string = '';
  @Prop() value: string = '';

  @Element() el: HTMLElement;
  childEl: HTMLElement;

  /** Dispatched when the button is clicked. */
  @Event({ eventName: 'click', composed: true, cancelable: true, bubbles: true }) clickCompleted: EventEmitter;

  clickCompletedHandler() {
    this.clickCompleted.emit();
  }

  @AttachInternals() internals: ElementInternals;

  @Watch('value')
  valueUpdated() {
    this.internals.setFormValue(this.value);
    if (this.value) {
      this.childEl?.setAttribute('value', this.value);
    } else {
      this.childEl?.removeAttribute('value');
    }
  }

  private verifiedButtonType = '';
  private validButtonTypes = ['filled-button', 'outlined-button', 'text-button'];

  @Watch('form')
  formUpdated() {
    if (this.form) {
      this.childEl?.setAttribute('form', this.form);
    } else {
      this.childEl?.removeAttribute('form');
    }
  }

  componentDidLoad() {
    this.el.querySelector(this.verifiedButtonType).addEventListener(
      'click',
      e => {
        e.stopPropagation();
        this.clickCompletedHandler();
      },
      { once: false },
    );
  }

  componentWillLoad(): void {
    this.verifiedButtonType = this.convertToMaterialButtonType(this.buttonType);
  }

  componentDidRender(): void {
    this.childEl = this.el.querySelector(this.verifiedButtonType) as HTMLElement;
    this.formUpdated();
    this.valueUpdated();

    const icon = this.el.querySelector('omk-icon');
    if (icon) {
      const mdIcon = icon.shadowRoot.querySelector('md-icon');
      if (mdIcon) mdIcon.style.fontSize = 'inherit';
    }
  }

  convertToMaterialButtonType(input: string): string {
    const type = this.validButtonTypes.includes(input.toLowerCase()) ? `md-${input}` : 'md-filled-button';
    return type;
  }

  render() {
    return (
      <Host disabled={this.disabled}>
        <this.verifiedButtonType
          part="button"
          aria-label={this.ariaLabel}
          type={this.type}
          trailing-icon={this.trailingIcon}
          name={this.name}
          value={this.value}
          class={{
            'btn': true,
            'ghost-mode': this.ghostMode,
            'btn-large': this.buttonSize.toLowerCase() === 'large',
            'btn-medium': this.buttonSize.toLowerCase() === 'medium',
            'btn-small': this.buttonSize.toLowerCase() === 'small',
          }}
          disabled={this.disabled}
        >
          <div>
            <span part="text">
              <slot></slot>
            </span>
          </div>

          <slot slot="icon" name="icon"></slot>
        </this.verifiedButtonType>
      </Host>
    );
  }
}
